// Typed client for the CodeBridge public API. Every call sends cookies
// (credentials: "include") so the session rides along; the API base is
// the Cloudflare-Tunnel origin, set at build time via VITE_API_BASE.

const BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/$/, "");

export class APIError extends Error {
  constructor(
    public status: number,
    message: string,
    // code is the machine-readable error string the API returns (e.g.
    // "server_in_use"); used by humanizeError to pick a friendly message
    // without ever showing raw backend text to the user.
    public code?: string,
  ) {
    super(message);
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(BASE + path, {
    method,
    credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  // Tolerate non-JSON bodies (proxy/edge error pages, gateway timeouts):
  // JSON.parse on HTML would otherwise throw and mask the real status.
  let data: any = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }
  if (!res.ok) {
    // The API returns `error` as either a string code/message or an
    // object {code?, message?}. Normalize to (message, code).
    const err = data?.error;
    if (err && typeof err === "object") {
      throw new APIError(res.status, err.message ?? err.code ?? res.statusText, err.code);
    }
    const s = typeof err === "string" ? err : res.statusText;
    throw new APIError(res.status, s, typeof err === "string" ? err : undefined);
  }
  return data as T;
}

export const apiBase = BASE;

// --- identity ---
export interface Workspace {
  tenant_id: string;
  name?: string;
  role: string;
  // Lifecycle status, present only for the CURRENT workspace: "" (grandfathered,
  // usable), "pending" (new, hard-paywalled until subscribed), "active".
  status?: string;
}
export interface Me {
  auth: "session" | "api_key";
  current_tenant_id: string;
  user_id?: string;
  email?: string;
  name?: string;
  two_factor_enabled?: boolean;
  workspaces?: Workspace[];
}
export const getMe = () => req<Me>("GET", "/v1/me");
export const requestMagicLink = (email: string) => req("POST", "/auth/magic/request", { email });
export const logout = () => req("POST", "/auth/logout");
export const ssoStartURL = (provider: "google" | "github") => `${BASE}/auth/${provider}/start`;

// switchWorkspace points the current session at another workspace the user
// belongs to. The caller should refetch /v1/me afterwards.
export const switchWorkspace = (tenantId: string) =>
  req<{ current_tenant_id: string }>("POST", "/v1/workspaces/switch", { tenant_id: tenantId });

// createWorkspace makes a new workspace owned by the caller and switches the
// session into it. The caller should do a full reload afterwards.
export const createWorkspace = (name: string) =>
  req<{ tenant_id: string; current_tenant_id: string }>("POST", "/v1/workspaces", { name });

// --- two-factor (TOTP) ---
export interface TOTPEnrollment {
  secret: string;
  otpauth_url: string;
}
// startTotp returns the QR/secret to add to an authenticator app (does not
// enable 2FA until confirmTotp succeeds).
export const startTotp = () => req<TOTPEnrollment>("POST", "/v1/account/totp/start");
// confirmTotp arms 2FA with a code and returns one-time recovery codes.
export const confirmTotp = (code: string) =>
  req<{ enabled: boolean; recovery_codes: string[] }>("POST", "/v1/account/totp/confirm", { code });
export const disableTotp = (code: string) =>
  req<{ disabled: boolean }>("POST", "/v1/account/totp/disable", { code });
export const regenerateRecoveryCodes = (code: string) =>
  req<{ recovery_codes: string[] }>("POST", "/v1/account/totp/recovery-codes", { code });
// verifyTotp completes the login gate (reads the cb_2fa challenge cookie).
export const verifyTotp = (code: string) =>
  req<{ status: string }>("POST", "/auth/totp/verify", { code });

// --- credits / billing ---
export interface BillingAccount {
  spendable_credits: number; // the single pool, after today's renewal
  cap_credits: number; // ceiling the daily renewal tops up toward
  daily_refill_credits: number; // added per day, clamped to the cap
  next_refill_at: string;
  lifetime_granted_credits: number;
  lifetime_spent_credits: number;
  enforcement_mode: "audit" | "soft" | "hard";
  blocked: boolean;
  low_balance: boolean;
  plan: string; // plan id ("" = unassigned)
  plan_name: string;
  stripe_managed: boolean; // has a Stripe customer ⇒ the Billing Portal is usable
}
export const getBillingAccount = () => req<BillingAccount>("GET", "/v1/billing/account");

// CreditTier is one selectable within-plan credit pool (same plan entitlements,
// different monthly credit allowance + price).
export interface CreditTier {
  credits: number;
  price_eur_cents: number; // monthly
  purchasable: boolean; // a monthly Stripe price is configured for this tier
  annual_price_eur_cents: number; // yearly-billed total (20% off)
  annual_purchasable: boolean; // an annual Stripe price is configured for this tier
}
// CatalogPlan is one subscription tier for the onboarding/upgrade picker.
export interface CatalogPlan {
  id: string;
  name: string;
  price_eur_cents: number; // default-tier price
  custom: boolean; // Enterprise: contact sales, no self-serve price
  purchasable: boolean; // the default monthly tier is purchasable ⇒ checkout works
  annual_purchasable: boolean; // the default annual tier is purchasable
  trial_days: number; // >0 → offered with a free trial (Pro = 7)
  current: boolean; // the workspace's currently-assigned plan
  monthly_credits: number; // default-tier credits
  daily_refresh_credits: number;
  tiers: CreditTier[]; // selectable credit tiers (>=1; [0] = default)
  seats: number; // -1 = unlimited
  agents: number; // -1 = unlimited
  scheduled_tasks: number;
  connected_servers: number;
  concurrent_missions: number; // 0 = missions off (gate)
  history_retention_days: number;
  scheduling: boolean;
  custom_domain: boolean;
  advanced_rbac: boolean;
  policy_enforce: boolean;
  per_agent_budgets: boolean;
  api_access: boolean;
  sso: boolean;
  stdio_openapi_integrations: boolean;
}
export const getBillingPlans = () =>
  req<{ plans: CatalogPlan[]; current_plan: string }>("GET", "/v1/billing/plans");

// startCheckout opens a Stripe Checkout session for a plan + chosen credit tier
// at the given billing interval, returning its URL (the caller redirects there).
// Owner only. credits selects the within-plan tier (0 ⇒ default); interval is
// "month" (default) or "year" (annual, discounted).
export const startCheckout = (plan: string, credits = 0, interval: "month" | "year" = "month") =>
  req<{ url: string }>("POST", "/v1/billing/checkout", { plan, credits, interval });

// openBillingPortal opens the Stripe billing portal (manage/cancel). Owner only.
export const openBillingPortal = () => req<{ url: string }>("POST", "/v1/billing/portal", {});

// changeSubscription changes an EXISTING subscription's plan/tier/interval in-app
// (owner only). effect: "immediate" (upgrade, prorated now), "period_end"
// (downgrade, applies at effective_at unix), or "none" (already on it). A 409
// {code:"no_subscription"} means the workspace must subscribe (checkout) first.
export const changeSubscription = (plan: string, credits = 0, interval: "month" | "year" = "month") =>
  req<{ effect: "immediate" | "period_end" | "none"; effective_at?: number }>(
    "POST",
    "/v1/billing/change",
    { plan, credits, interval },
  );

// redeemCode redeems a self-hosted promo code, activating the workspace on the
// code's plan (no Stripe). Owner only.
export const redeemCode = (code: string) =>
  req<{ plan: string; plan_name: string }>("POST", "/v1/billing/redeem", { code });

export interface UsageEntry {
  id: string;
  ts: string;
  kind: string;
  source: string;
  agent?: string; // which agent (display name)
  channel?: string; // dashboard|telegram|slack (agent turns)
  description: string; // which action/session
  credits: number; // signed: + grant, − debit
  balance_after_credits: number;
  model?: string;
}
export const listUsage = (opts?: { cursor?: string; source?: string; projectId?: string; limit?: number }) => {
  const q = new URLSearchParams();
  if (opts?.cursor) q.set("cursor", opts.cursor);
  if (opts?.source) q.set("source", opts.source);
  if (opts?.projectId) q.set("project_id", opts.projectId);
  if (opts?.limit) q.set("limit", String(opts.limit));
  const qs = q.toString();
  return req<{ entries: UsageEntry[]; next_cursor?: string }>("GET", `/v1/billing/usage${qs ? "?" + qs : ""}`);
};

export interface AgentSpend {
  project_id: string;
  name: string;
  credits: number;
  daily_budget_credits: number;
  monthly_budget_credits: number;
  daily_spent_credits: number;
  monthly_spent_credits: number;
}
export const listSpendByAgent = (days?: number) =>
  req<{ agents: AgentSpend[] }>("GET", `/v1/billing/by-agent${days ? `?days=${days}` : ""}`);

// SpendLine is one bucket of the usage breakdown ("where it went").
export interface SpendLine {
  type: string; // scheduled|conversation|channel|infra|buildai
  label: string;
  kind?: string; // channel kind for type=channel
  credits: number;
}
export const listBreakdown = (days?: number) =>
  req<{ total_credits: number; lines: SpendLine[] }>("GET", `/v1/billing/breakdown${days ? `?days=${days}` : ""}`);
// setAgentBudget sets an agent's daily + monthly spending budget (whole credits; 0 clears).
export const setAgentBudget = (projectId: string, dailyCredits: number, monthlyCredits: number) =>
  req<{ daily_credits: number; monthly_credits: number }>(
    "PUT",
    `/v1/billing/agents/${projectId}/budget`,
    { daily_credits: dailyCredits, monthly_credits: monthlyCredits },
  );

// --- servers ---
export type AuthMethod =
  | "none"
  | "bearer"
  | "api_key"
  | "basic"
  | "headers"
  | "oauth2_client_credentials"
  | "oauth2_authorization_code";

// AuthEnvelope mirrors the server's serverauth.Envelope JSON union.
export interface AuthEnvelope {
  method: AuthMethod;
  bearer?: { token: string };
  api_key?: { header: string; value: string };
  basic?: { username: string; password: string };
  headers?: Record<string, string>;
  oauth2_cc?: { token_url: string; client_id: string; client_secret?: string; scopes?: string[]; resource?: string };
  oauth2_ac?: {
    authorize_url?: string;
    token_url?: string;
    client_id?: string;
    client_secret?: string;
    scopes?: string[];
  };
}

export interface Server {
  name: string;
  // kind distinguishes an MCP server ("http", or "" for legacy rows) from an
  // OpenAPI/REST/cURL integration ("openapi"). MCP servers carry upstream_url;
  // openapi integrations carry base_url with upstream_url empty.
  kind: string;
  upstream_url: string;
  base_url?: string;
  auth_method: AuthMethod | "";
  connected: boolean;
  tools: string[];
  // building is true while a stdio/npx package is still being built+introspected
  // in the background (connect returns immediately). build_error carries the
  // reason a stdio build failed. Both absent for connected/non-stdio servers.
  building?: boolean;
  build_error?: string;
  // package/version are the stdio kind's package coordinates (absent otherwise),
  // shown as the integration's target and used to prefill a retry. runtime is
  // the stdio build family ("npm" | "python"); absent/"npm" for legacy rows.
  package?: string;
  version?: string;
  runtime?: string;
  // openapi kind's stored spec source, used to prefill an edit (a connect
  // overwrite re-supplies the source). Exactly one of spec_url/spec_text/curl
  // is set. Secrets are never here — the auth envelope stays sealed.
  spec_url?: string;
  spec_text?: string;
  curl?: string;
  instructions?: string;
}

// ServerTool is one tool's full contract — name + description + the raw
// JSON Schema for its arguments (an arbitrary JSON object, so `unknown`).
export interface ServerTool {
  name: string;
  description: string;
  input_schema?: unknown;
}
// ServerDetail is the full record for one server (the list endpoint returns
// tool names only; this carries descriptions + schemas + timestamps).
export interface ServerDetail {
  name: string;
  kind: string;
  upstream_url: string;
  base_url?: string;
  auth_method: AuthMethod | "";
  connected: boolean;
  tools: ServerTool[];
  created_at: string;
  updated_at: string;
  // Kind-specific config (for the detail view + edit prefill). stdio coords +
  // egress; secrets (env values) are never returned.
  package?: string;
  version?: string;
  runtime?: string; // stdio build family ("npm" | "python")
  pinned_version?: string;
  egress_allow?: string[];
  // egress_targets are the (host, port) backends the package may reach via the
  // host-side L4 broker (e.g. a MongoDB cluster on :27017). Not secret — the edit
  // form prefills + round-trips them.
  egress_targets?: EgressTarget[];
  // env_keys are the NAMES (never values) of the stdio server's sealed env vars,
  // so the edit form can show what's configured and keep/replace/delete per key.
  env_keys?: string[];
  // env_values returns ONLY the user-typed plaintext inline env values, keyed by
  // name, so the unified env-var edit form can prefill its plaintext rows. Vault
  // secret bytes are NEVER returned (those surface only as refs in
  // attached_secrets). Shares its keyset with env_keys (same decrypted map).
  env_values?: Record<string, string>;
  // args_redacted are the sealed launch args with values masked (flag names shown,
  // any value/positional redacted) so the edit form can show + manage them
  // per-position without exposing a secret embedded in an arg.
  args_redacted?: string[];
  // attached_secrets are the vault attachment refs (no bytes) so the edit form can
  // prefill + round-trip the full set (add/remove/full-detach all work).
  attached_secrets?: SecretRef[];
  // env_unavailable is true when the sealed config couldn't be read (decrypt
  // error): the edit form must then NOT manage env/args (to avoid wiping secrets).
  env_unavailable?: boolean;
  building?: boolean;
  build_error?: string;
  // update_error is set when a CONNECTED stdio server's last rebuild (version-bump
  // edit) failed — still serving the previous build, so it's a note, not "down".
  update_error?: string;
}
export const getServer = (name: string) =>
  req<ServerDetail>("GET", `/v1/servers/${encodeURIComponent(name)}`);
export const listServers = () => req<{ servers: Server[] }>("GET", "/v1/servers").then((r) => r.servers ?? []);

export interface ConnectResult {
  connected: boolean;
  oauth_required?: boolean;
  authorize_url?: string;
}

// OpenAPISpecInput drives kind:"openapi" connects: the upstream is described
// by an OpenAPI 3.x spec (URL or pasted JSON/YAML) or a single cURL command,
// which the server parses into MCP tools + an execution IR. base_url overrides
// the spec's servers[]; selected_ops/mode are optional refinements.
export interface OpenAPISpecInput {
  spec_url?: string;
  spec_text?: string;
  curl?: string;
  base_url?: string;
  selected_ops?: string[];
  mode?: string;
  // Plain-English description of a Form/cURL endpoint; becomes the tool
  // description so the connecting agent knows how to build the request.
  instructions?: string;
}

// StdioSpecInput drives kind:"stdio" connects: a package that Berth builds in
// an isolated VM and introspects. runtime "npm" (default) installs an npm
// package (npm install --ignore-scripts); "python" installs a PyPI package (uv).
export interface StdioSpecInput {
  package: string;
  version?: string;
  runtime?: "npm" | "python";
  // env is the package's runtime process environment (secrets like API keys),
  // sealed server-side into the integration's auth envelope. On an env_managed
  // edit these are the changed/new keys only.
  env?: Record<string, string>;
  // env_keep is a legacy wire field (kept for compatibility): it listed existing
  // env keys to retain unchanged on an edit, back when the form couldn't echo
  // secret values. The unified env-var form now prefills plaintext values from
  // env_values and always re-sends them, so it never emits env_keep.
  env_keep?: string[];
  // env_managed signals per-key env management: result = kept + changed/new, any
  // other existing key dropped. Without it, env present = replace, empty = keep.
  env_managed?: boolean;
  // args are extra CLI arguments appended to the package's launch command
  // (e.g. ["--readOnly", "--connectionString=…"]). Sealed like env. On an
  // args_managed edit, args is ignored in favor of arg_items.
  args?: string[];
  // arg_items drives per-position args management on an edit: an ordered list
  // where each item either keeps an existing arg (keep_index) or sets a new/
  // replaced literal (value). Honored only with args_managed.
  arg_items?: StdioArgItem[];
  // args_managed signals per-position args management: result is built from
  // arg_items in order. Without it, args present = replace, empty = keep.
  args_managed?: boolean;
  // egress_targets are the (host, port) backends this package may reach via the
  // host-side L4 broker. Not secret, so the edit form prefills + sends the full
  // desired list. Rule: omitted (undefined) = keep existing; present (incl. [])
  // = replace — so a non-egress edit can't wipe them.
  egress_targets?: EgressTarget[];
  // secret_attachments binds vault secrets to this package's runtime (refs only,
  // no values). Same keep/replace rule as egress_targets.
  secret_attachments?: SecretRef[];
}

// SecretRef attaches a vault secret to an integration's runtime: mode "env" sets
// var_name to the secret's value; mode "file" writes the secret to a file and
// points var_name at the path (e.g. GOOGLE_APPLICATION_CREDENTIALS).
export interface SecretRef {
  secret_id: string;
  mode: "env" | "file";
  var_name: string;
}

// StdioArgItem is one entry in an args_managed edit: keep_index reuses the
// existing arg at that position (preserving a secret value the form can't echo),
// or value sets a new/replaced literal. Exactly one is set.
export interface StdioArgItem {
  keep_index?: number;
  value?: string;
}

// EgressTarget is one allowed (host, port) backend for a stdio package's L4
// egress. host follows the proxy's exact / "*." wildcard rule.
export interface EgressTarget {
  host: string;
  port: number;
}

export const connectServer = (
  name: string,
  upstream_url: string,
  auth: AuthEnvelope,
  opts?: { kind?: string; openapi?: OpenAPISpecInput; stdio?: StdioSpecInput },
) =>
  req<ConnectResult>("POST", "/v1/servers", {
    name,
    upstream_url,
    auth,
    ...(opts?.kind ? { kind: opts.kind } : {}),
    ...(opts?.openapi ? { openapi: opts.openapi } : {}),
    ...(opts?.stdio ? { stdio: opts.stdio } : {}),
  });

export interface ProbeResult {
  suggested_method: AuthMethod;
  oauth: boolean;
  supports_dcr: boolean;
  scopes?: string[];
}
export const probeServer = (upstream_url: string) =>
  req<ProbeResult>("POST", "/v1/servers/probe", { upstream_url });

export interface ToolDiff {
  added: string[];
  removed: string[];
  changed: string[];
}
export type RefreshResult =
  | {
      kind: "ok";
      connected: boolean;
      tools: string[];
      diff: ToolDiff;
      affected_projects: number;
      redeploying: number;
      redeploy_in_progress?: boolean;
    }
  // stdio/npm refresh kicks off a background package rebuild; the dashboard
  // shows "refreshing" and the building-poll picks up the outcome.
  | { kind: "building" }
  | { kind: "reauth"; authorize_url: string };

// Dedicated fetch (not req<T>) because the 409 reauth_required body carries
// authorize_url, which req() discards on non-2xx (it keeps only `error`).
export async function refreshServer(name: string): Promise<RefreshResult> {
  // Abort safety net: even though stdio refresh now returns immediately and the
  // http/openapi introspect is bounded server-side, a lost response (an edge
  // proxy dropping the connection) must surface as an error and clear the
  // spinner rather than hang forever.
  const ctl = new AbortController();
  const timer = setTimeout(() => ctl.abort(), 30_000);
  let res: Response;
  try {
    res = await fetch(BASE + `/v1/servers/${encodeURIComponent(name)}/refresh`, {
      method: "POST",
      credentials: "include",
      signal: ctl.signal,
    });
  } finally {
    clearTimeout(timer);
  }
  const text = await res.text();
  let data: any = {};
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
  }
  if (res.ok) {
    if (data?.building === true) return { kind: "building" };
    const diff = data?.diff ?? {};
    return {
      kind: "ok",
      connected: !!data?.connected,
      tools: Array.isArray(data?.tools) ? data.tools : [],
      diff: {
        added: Array.isArray(diff.added) ? diff.added : [],
        removed: Array.isArray(diff.removed) ? diff.removed : [],
        changed: Array.isArray(diff.changed) ? diff.changed : [],
      },
      affected_projects: typeof data?.affected_projects === "number" ? data.affected_projects : 0,
      redeploying: typeof data?.redeploying === "number" ? data.redeploying : 0,
      redeploy_in_progress: data?.redeploy_in_progress === true,
    };
  }
  if (res.status === 409 && data?.error === "reauth_required" && typeof data?.authorize_url === "string") {
    return { kind: "reauth", authorize_url: data.authorize_url };
  }
  throw new APIError(res.status, data?.error ?? res.statusText);
}

// delete-impact previews what a DELETE would do without performing it:
// `emptied` non-empty means the delete would leave those projects with no
// tools, so the API blocks it (409 server_in_use). Pre-checking here lets
// the UI never even attempt a blocked delete.
export interface DeleteImpact {
  affected: string[];
  emptied: string[];
}
export const getDeleteImpact = (name: string) =>
  req<DeleteImpact>("GET", `/v1/servers/${encodeURIComponent(name)}/delete-impact`);

export interface DeleteServerResult {
  deleted: boolean;
  redeploying: number;
  redeploy_in_progress?: boolean;
}
export const deleteServer = (name: string) =>
  req<DeleteServerResult>("DELETE", `/v1/servers/${encodeURIComponent(name)}`);

// rebuildServer retries a failed npm/stdio build using the server's stored
// credentials (no secrets re-sent). Returns once the rebuild is queued; the
// caller polls listServers() for the "building" → connected/failed transition.
export const rebuildServer = (name: string) =>
  req<{ building: boolean }>("POST", `/v1/servers/${encodeURIComponent(name)}/rebuild`);

// --- deployments ---
export interface Deployment {
  deployment_id: string;
  berth_project_id: string;
  url: string;
  created_at: string;
  stale: boolean;
  subdomain?: string;
  name?: string;
  // Cosmetic identity. emoji is a single glyph (else a name-monogram default);
  // color is an AgentColor palette token (else a name-hashed default).
  emoji?: string;
  color?: string;
}
export const listDeployments = () =>
  req<{ deployments: Deployment[] }>("GET", "/v1/deployments").then((r) => r.deployments ?? []);
// setAgentAppearance sets an agent's emoji + color (empty values clear → default).
export const setAgentAppearance = (projectId: string, a: { emoji: string; color: string }) =>
  req<{ emoji: string; color: string }>(
    "PATCH",
    `/v1/deployments/${encodeURIComponent(projectId)}/appearance`,
    a,
  );
// deleteProject removes a project entirely (Berth teardown + local cleanup).
// Owner-only server-side; irreversible.
export const deleteProject = (projectId: string) =>
  req("DELETE", `/v1/projects/${encodeURIComponent(projectId)}`);
export interface DeployResult {
  project_id: string;
  deployment_id: string;
  url: string;
  // default_token is the project's default service-account /mcp bearer,
  // present ONLY on a fresh project's first deploy (show once). Empty on
  // redeploy or when an existing project's legacy token was adopted.
  default_token?: string;
}

// SelectedServer is one entry of a project's MCP selection. all_tools:true
// means "every current and future tool" (tools omitted); all_tools:false
// pins a frozen `tools` list — new upstream tools don't auto-join. A server
// absent from the array is excluded entirely.
export interface SelectedServer {
  name: string;
  all_tools: boolean;
  tools?: string[];
}
export interface ProjectSelection {
  servers: SelectedServer[];
}

export const deploy = (name: string, subdomain: string, project_id?: string, selection?: ProjectSelection) =>
  req<DeployResult>("POST", "/v1/deploy", {
    name,
    subdomain,
    project_id,
    ...(selection !== undefined ? { selection } : {}),
  });

// getProjectSelection returns the project's stored selection, or null for
// the canonical "all servers + all tools" (e.g. legacy projects). req()
// turns an empty/null JSON body into {}, so a missing `servers` is read as
// the same "all" → null.
export const getProjectSelection = (projectId: string) =>
  req<ProjectSelection | null>("GET", `/v1/projects/${encodeURIComponent(projectId)}/selection`).then((s) =>
    s && Array.isArray(s.servers) ? s : null,
  );

export const updateStaleProjects = () =>
  req<{ started: number }>("POST", "/v1/deployments/update-stale");

// restartAgent reboots an existing agent's sandbox onto the current runtime
// image, keeping its URL, access tokens, selected tools, playbook, and storage
// unchanged (server re-applies the stored selection). Use it to pick up a new
// runtime — distinct from deploy()/update, which can change the served tools.
export const restartAgent = (projectId: string) =>
  req<DeployResult>("POST", `/v1/deployments/${encodeURIComponent(projectId)}/restart`);

// --- custom domains ---
export interface CustomDomainRecord {
  type: string; // CNAME | TXT
  name: string;
  value: string;
}
export interface CustomDomainDNS {
  // routing is a CNAME (subdomain) or A record (apex) pointing the domain at us.
  routing: CustomDomainRecord;
  txt: CustomDomainRecord;
}
// CustomDomain status: pending | verifying | verified | active | degraded | failed.
export interface CustomDomain {
  id: string;
  domain: string;
  status: string;
  cert_status?: string;
  last_error?: string;
  dns_records?: CustomDomainDNS;
}
const projPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/custom-domains`;
export const listCustomDomains = (projectId: string) =>
  req<{ domains: CustomDomain[] }>("GET", projPath(projectId)).then((r) => r.domains ?? []);
export const addCustomDomain = (projectId: string, domain: string) =>
  req<CustomDomain>("POST", projPath(projectId), { domain });
export const verifyCustomDomain = (projectId: string, domain: string) =>
  req<CustomDomain>("POST", `${projPath(projectId)}/${encodeURIComponent(domain)}/verify`);
export const deleteCustomDomain = (projectId: string, domain: string) =>
  req("DELETE", `${projPath(projectId)}/${encodeURIComponent(domain)}`);

// --- members + invites ---
export interface Member {
  user_id: string;
  email?: string;
  name?: string;
  role: string;
  joined_at: string;
}
export const listMembers = () => req<{ members: Member[] }>("GET", "/v1/members").then((r) => r.members ?? []);
export const removeMember = (userID: string) => req("DELETE", `/v1/members/${userID}`);
// setMemberRole changes a member's workspace-level (org) role. Owner-only
// server-side. Roles: owner | admin | editor | viewer.
export const setMemberRole = (userID: string, role: string) =>
  req("PUT", `/v1/members/${userID}`, { role });
export interface Invite {
  id: string;
  email: string;
  role: string;
  created_at: string;
  expires_at: string;
  accepted: boolean;
}
export const listInvites = () => req<{ invites: Invite[] }>("GET", "/v1/invites").then((r) => r.invites ?? []);
export const createInvite = (email: string, role: string) => req("POST", "/v1/invites", { email, role });
export const revokeInvite = (id: string) => req("DELETE", `/v1/invites/${id}`);

// --- access tokens (per-project /mcp credentials) ---
export interface AccessToken {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
}
const atPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/access-tokens`;
export const listAccessTokens = (projectId: string) =>
  req<{ access_tokens: AccessToken[] }>("GET", atPath(projectId)).then((r) => r.access_tokens ?? []);
// create/rotate return the plaintext token exactly once.
export const createAccessToken = (projectId: string, name: string) =>
  req<{ access_token: AccessToken; token: string }>("POST", atPath(projectId), { name });
export const rotateAccessToken = (projectId: string, id: string) =>
  req<{ access_token: AccessToken; token: string }>("POST", `${atPath(projectId)}/${encodeURIComponent(id)}/rotate`);
export const deleteAccessToken = (projectId: string, id: string) =>
  req("DELETE", `${atPath(projectId)}/${encodeURIComponent(id)}`);

// --- secrets vault (workspace-scoped, reusable across integrations) ---

export interface Secret {
  id: string;
  name: string;
  kind: "value" | "file";
  file_name?: string;
  byte_len: number;
  version: number;
  created_at: string;
}
export const listSecrets = () => req<{ secrets: Secret[] }>("GET", "/v1/secrets").then((r) => r.secrets ?? []);
// Values are never returned — create yields only the metadata view.
export const createSecret = (b: { name: string; kind: "value" | "file"; value?: string; file_b64?: string; file_name?: string }) =>
  req<Secret>("POST", "/v1/secrets", b);
// Rotate re-seals the secret in place (kind is fixed) and restarts the
// integrations that use it; returns the restarted server names.
export const rotateSecret = (id: string, b: { value?: string; file_b64?: string; file_name?: string }) =>
  req<{ secret: Secret; restarted_servers: string[] }>("PUT", `/v1/secrets/${encodeURIComponent(id)}`, b);
export const deleteSecret = (id: string) => req("DELETE", `/v1/secrets/${encodeURIComponent(id)}`);

// --- notification channels (per-project approval delivery targets) ---
export interface Channel {
  id: string;
  name: string;
  kind: string;
  enabled: boolean;
  role?: string; // "" operator (notifications) | "agent" (customer ⇄ agent chat)
  created_at: string;
}
// The secret (bot token / chat id / signing secret) is write-only: supplied on
// create/update, never returned. Rotation = supply a new one.
export interface ChannelInput {
  name: string;
  kind: string;
  enabled?: boolean;
  role?: string;
  telegram?: { bot_token: string; chat_id: string };
  slack?: { bot_token: string; signing_secret: string; channel: string };
}
const chPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/channels`;
export const listChannels = (projectId: string) =>
  req<{ channels: Channel[]; kinds: string[] }>("GET", chPath(projectId)).then((r) => ({
    channels: r.channels ?? [],
    kinds: r.kinds ?? [],
  }));
export const createChannel = (projectId: string, body: ChannelInput) =>
  req<{ channel: Channel }>("POST", chPath(projectId), body);
export const updateChannel = (projectId: string, id: string, body: Partial<ChannelInput>) =>
  req<{ channel: Channel }>("PATCH", `${chPath(projectId)}/${encodeURIComponent(id)}`, body);
export const deleteChannel = (projectId: string, id: string) =>
  req("DELETE", `${chPath(projectId)}/${encodeURIComponent(id)}`);
export const testChannel = (projectId: string, id: string) =>
  req<{ ok: boolean }>("POST", `${chPath(projectId)}/${encodeURIComponent(id)}/test`);

// --- per-project member grants ---
export interface ProjectMember {
  user_id: string;
  email: string;
  org_role: string;
  project_grant?: string; // "" when none
  effective_role: string;
}
const pmPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/members`;
export const listProjectMembers = (projectId: string) =>
  req<{ members: ProjectMember[] }>("GET", pmPath(projectId)).then((r) => r.members ?? []);
export const setProjectMember = (projectId: string, userID: string, role: string) =>
  req("PUT", `${pmPath(projectId)}/${encodeURIComponent(userID)}`, { role });
export const removeProjectMember = (projectId: string, userID: string) =>
  req("DELETE", `${pmPath(projectId)}/${encodeURIComponent(userID)}`);

// --- project storage (skills/state volumes) ---
export type StorageVolume = "skills" | "state" | "playbook";
export interface StorageEntry {
  name: string;
  is_dir: boolean;
  size: number;
}
export interface StorageUsage {
  total_bytes: number;
  free_bytes: number;
  used_bytes: number;
}
export interface StorageListing {
  volume: string;
  path: string;
  entries: StorageEntry[];
  usage: StorageUsage;
}
export interface StorageFile {
  name: string;
  size: number;
  truncated: boolean;
  content_b64: string;
}
const stPath = (projectId: string, volume: StorageVolume) =>
  `/v1/projects/${encodeURIComponent(projectId)}/storage/${volume}`;

export const listStorage = (projectId: string, volume: StorageVolume, path = "") =>
  req<StorageListing>(
    "GET",
    `${stPath(projectId, volume)}${path ? `?path=${encodeURIComponent(path)}` : ""}`,
  ).then((r) => ({ ...r, entries: r.entries ?? [] }));

export const readStorage = (projectId: string, volume: StorageVolume, path: string) =>
  req<StorageFile>("GET", `${stPath(projectId, volume)}/file?path=${encodeURIComponent(path)}`);

// writeStorage sends the raw file bytes (PUT body). content may be a string
// (editor save) or Blob/ArrayBuffer (upload). May wake the project — the call
// can take a few seconds.
export async function writeStorage(
  projectId: string,
  volume: StorageVolume,
  path: string,
  content: string | Blob | ArrayBuffer,
): Promise<void> {
  const res = await fetch(`${BASE}${stPath(projectId, volume)}/file?path=${encodeURIComponent(path)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/octet-stream" },
    body: content,
  });
  if (!res.ok) {
    const text = await res.text();
    let data: any = {};
    try {
      data = JSON.parse(text);
    } catch {
      data = {};
    }
    throw new APIError(res.status, data?.error ?? res.statusText);
  }
}

export const deleteStorage = (projectId: string, volume: StorageVolume, path: string) =>
  req("DELETE", `${stPath(projectId, volume)}/file?path=${encodeURIComponent(path)}`);

// --- AI playbook author (chat with a server-side tool loop) ---

export type ChatRole = "user" | "assistant";

// ChatMessage is one Anthropic-shaped message. content is opaque (a string for
// plain turns, or a content-block array for the assistant's tool_use turns and
// the matching tool_result turns) and is replayed to the server verbatim.
export interface ChatMessage {
  role: ChatRole;
  content: unknown;
}

export interface ChatToolCall {
  id: string;
  name: string;
  input: unknown;
}

export interface ChatToolResult {
  id: string;
  name: string;
  ok: boolean;
  path: string;
  summary: string;
}

// ChatFile is a draft mutation the server made (its tools never persist; the
// client holds the draft and saves it explicitly).
export interface ChatFile {
  path: string;
  content: string;
  deleted: boolean;
}

// ChatQuestion is a structured clarifying question (rendered as tappable options
// in the chat); the user's pick is replied as a tool_result for `id`.
export interface ChatQuestion {
  id: string;
  question: string;
  options: string[];
  multi: boolean;
}

// GovernancePlan is the AI's proposed authorization policy for a NEW agent
// (Build with AI). It carries NO secrets — channel tokens are user-supplied at
// review. Applied at Deploy, in audit mode, via applyGovernance.
export interface GovernanceRule {
  intent_label: string;
  tool: string; // "server.tool" or "server.*"
  action: PolicyAction;
  reason?: string;
}
export interface GovernancePlan {
  default_action: PolicyDefaultAction;
  rules: GovernanceRule[];
  channel?: { kind: "telegram"; name: string; purpose?: string };
}

export interface ChatHandlers {
  onText: (text: string) => void;
  onToolCall: (call: ChatToolCall) => void;
  onToolResult: (result: ChatToolResult) => void;
  onFile: (file: ChatFile) => void;
  onQuestion: (question: ChatQuestion) => void;
  onGovernance?: (plan: GovernancePlan) => void;
  onDone: (appended: ChatMessage[]) => void;
  onError: (message: string) => void;
}

// streamPlaybookChat POSTs the full conversation + the current draft and consumes
// the server's SSE stream: text fragments, tool calls + results, file events
// (draft mutations), then a done event carrying the messages to append.
export async function streamPlaybookChat(
  projectId: string | null,
  messages: ChatMessage[],
  draft: ChatFile[],
  handlers: ChatHandlers,
  signal?: AbortSignal,
): Promise<void> {
  // projectId == null is the "Build with AI" create flow — no agent exists yet,
  // so hit the project-less endpoint (tools = all connected servers; file ops
  // touch only the draft).
  const url = projectId
    ? `${BASE}/v1/projects/${encodeURIComponent(projectId)}/playbook/chat`
    : `${BASE}/v1/playbook/chat`;
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages, draft }),
      signal,
    });
  } catch (e) {
    if ((e as Error).name !== "AbortError") handlers.onError("Couldn't reach the assistant.");
    return;
  }
  if (!res.ok || !res.body) {
    let msg = "The assistant is unavailable. Try again.";
    try {
      const j = await res.json();
      if (j?.error) msg = j.error;
    } catch {
      /* keep generic */
    }
    handlers.onError(msg);
    return;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  let terminated = false; // saw a done/error frame
  for (;;) {
    let chunk: ReadableStreamReadResult<Uint8Array>;
    try {
      chunk = await reader.read();
    } catch (e) {
      if ((e as Error).name !== "AbortError") handlers.onError("The connection dropped.");
      return;
    }
    if (chunk.done) break;
    buf += dec.decode(chunk.value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop() ?? "";
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      let ev: any;
      try {
        ev = JSON.parse(line.slice(5).trim());
      } catch {
        continue;
      }
      if (typeof ev.text === "string") handlers.onText(ev.text);
      else if (ev.tool_call) handlers.onToolCall(ev.tool_call);
      else if (ev.tool_result) handlers.onToolResult(ev.tool_result);
      else if (ev.file) handlers.onFile(ev.file);
      else if (ev.question) handlers.onQuestion(ev.question);
      else if (ev.governance) handlers.onGovernance?.(ev.governance);
      else if (ev.done) {
        terminated = true;
        handlers.onDone(ev.appended ?? []);
      } else if (ev.error) {
        terminated = true;
        handlers.onError(ev.error);
      }
    }
  }
  // The stream closed cleanly but never sent a terminal frame (proxy idle
  // timeout, server restart mid-turn) — guarantee a terminal callback so the
  // UI doesn't hang in a loading state.
  if (!terminated) handlers.onError("The connection closed unexpectedly.");
}

// --- authorization policy (per-project) ---
export type PolicyAction = "ALLOW" | "DENY" | "HOLD";
export type PolicyMode = "audit" | "enforce";
// PolicyDefaultAction is the verdict for a call no rule matched: "" or "ALLOW"
// = permit everything (a normal MCP endpoint), "DENY" = allow-list (block
// unless an explicit ALLOW), "HOLD" = hold-all (require approval unless an
// explicit ALLOW). "" and "ALLOW" are equivalent ("" is the unset default).
export type PolicyDefaultAction = "" | "ALLOW" | "DENY" | "HOLD";
export interface ArgCondition {
  path: string;
  op: string; // eq|neq|gt|lt|gte|lte|in|matches|exists
  value?: unknown;
}
export interface PolicyMatch {
  principal?: string; // service-account id; "" = any
  tool?: string; // "server.tool" or "server.*"; "" = any
  args?: ArgCondition[];
}
// PolicyApproval is the HOLD-action routing: which channel(s) get the approval
// request (empty = all enabled), the required approver (empty = any admin), and
// the grant TTL. channels are NotificationChannel ids.
export interface PolicyApproval {
  approver?: string;
  channels?: string[];
  ttl_secs?: number;
}
export interface PolicyRule {
  id: string;
  priority: number;
  match: PolicyMatch;
  action: PolicyAction;
  reason: string;
  enabled: boolean;
  approval?: PolicyApproval;
  created_at?: string;
}
// PolicyRuleInput is the writable shape (server owns id/timestamps).
export interface PolicyRuleInput {
  priority: number;
  match: PolicyMatch;
  action: PolicyAction;
  reason: string;
  enabled: boolean;
  approval?: PolicyApproval;
}
const polPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/policy`;
export const getPolicy = (projectId: string) =>
  req<{ mode: PolicyMode; default_action?: PolicyDefaultAction; capture: boolean; rules: PolicyRule[] }>(
    "GET",
    polPath(projectId),
  );
export const setPolicyMode = (projectId: string, mode: PolicyMode) =>
  req<{ mode: PolicyMode }>("PUT", `${polPath(projectId)}/mode`, { mode });
export const setPolicyDefault = (projectId: string, default_action: PolicyDefaultAction) =>
  req<{ default_action: PolicyDefaultAction }>("PUT", `${polPath(projectId)}/default`, { default_action });
export const setCapturePayloads = (projectId: string, capture: boolean) =>
  req<{ capture: boolean }>("PUT", `${polPath(projectId)}/capture`, { capture });

// Built-in /mcp tools every agent exposes regardless of connected servers.
// Order is the display order; must match the server's canonical list.
export const BUILTIN_TOOLS = [
  "execute_code",
  "read_file",
  "write_file",
  "list_directory",
  "publish_view",
  "ask_human",
  "get_answer",
  "check_approval",
] as const;
export type BuiltinTool = (typeof BUILTIN_TOOLS)[number];
export type BuiltinTools = Record<string, boolean>;

const baseToolsPath = (projectId: string) =>
  `/v1/projects/${encodeURIComponent(projectId)}/base-tools`;
export const getBaseTools = (projectId: string) =>
  req<{ tools: BuiltinTools }>("GET", baseToolsPath(projectId));
export const setBaseTools = (projectId: string, tools: BuiltinTools) =>
  req<{ tools: BuiltinTools }>("PUT", baseToolsPath(projectId), { tools });

// Scheduled / recurring agent tasks (a durable timer fires the payload back into
// the agent's conversation at fire_at). Sensitive fields (tenant, conversation
// ref) are omitted server-side; only channel_kind is surfaced.
export type ScheduleRecurrence = "once" | "cron";
export type ScheduleStatus =
  | "scheduled"
  | "firing"
  | "done"
  | "failed"
  | "cancelled"
  | "paused";
export interface Schedule {
  id: string;
  project_id: string;
  channel_kind: string;
  payload: string;
  recurrence: ScheduleRecurrence;
  cron?: string;
  timezone?: string;
  fire_at: string;
  until?: string;
  max_runs?: number;
  status: ScheduleStatus;
  runs_count: number;
  last_fired_at?: string;
  last_error?: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}
// ScheduleEditInput edits the payload + timing + end-bounds. The delivery target
// is fixed (recreate to retarget). until/max_runs use the server's preserve/clear/
// set semantics: omit → preserve, "" / 0 → clear, value → set.
export interface ScheduleEditInput {
  payload: string;
  recurrence: ScheduleRecurrence;
  fire_at?: string; // RFC3339, for once
  cron?: string; // 5-field, for cron
  timezone?: string; // IANA
  preset?: string; // "daily" | "weekly" (compiles to cron)
  preset_time?: string; // "HH:MM"
  preset_weekday?: number; // 0-6 (Sun-Sat)
  until?: string; // RFC3339 end bound, "" clears
  max_runs?: number; // 0 = unlimited
}
// ScheduleCreateInput creates a schedule that fires into an agent-role channel.
// channel_id picks the channel; the server resolves the recipient from its sealed
// config (the chat id never reaches the dashboard).
export interface ScheduleCreateInput {
  channel_id: string;
  payload: string;
  recurrence: ScheduleRecurrence;
  fire_at?: string;
  cron?: string;
  timezone?: string;
  preset?: string;
  preset_time?: string;
  preset_weekday?: number;
  until?: string;
  max_runs?: number;
}
const schedPath = (projectId: string) =>
  `/v1/projects/${encodeURIComponent(projectId)}/schedules`;
export const listSchedules = (projectId: string) =>
  req<{ schedules: Schedule[] }>("GET", schedPath(projectId)).then((r) => r.schedules ?? []);
export const createSchedule = (projectId: string, input: ScheduleCreateInput) =>
  req<Schedule>("POST", schedPath(projectId), input);
export const cancelSchedule = (projectId: string, id: string) =>
  req<{ cancelled: boolean }>("DELETE", `${schedPath(projectId)}/${encodeURIComponent(id)}`);
export const finishAfterNextSchedule = (projectId: string, id: string) =>
  req<{ schedule: Schedule }>(
    "POST",
    `${schedPath(projectId)}/${encodeURIComponent(id)}/finish-after-next`,
  ).then((r) => r.schedule);
export const pauseSchedule = (projectId: string, id: string) =>
  req<{ schedule: Schedule }>("POST", `${schedPath(projectId)}/${encodeURIComponent(id)}/pause`).then(
    (r) => r.schedule,
  );
export const resumeSchedule = (projectId: string, id: string) =>
  req<{ schedule: Schedule }>("POST", `${schedPath(projectId)}/${encodeURIComponent(id)}/resume`).then(
    (r) => r.schedule,
  );
export const skipNextSchedule = (projectId: string, id: string) =>
  req<{ schedule: Schedule }>(
    "POST",
    `${schedPath(projectId)}/${encodeURIComponent(id)}/skip-next`,
  ).then((r) => r.schedule);
export const updateSchedule = (projectId: string, id: string, input: ScheduleEditInput) =>
  req<{ schedule: Schedule }>("PATCH", `${schedPath(projectId)}/${encodeURIComponent(id)}`, input).then(
    (r) => r.schedule,
  );

// ── Autonomous missions ──────────────────────────────────────────────────────
export type MissionStatus =
  | "planning"
  | "awaiting_plan_approval"
  | "active"
  | "ticking"
  | "blocked"
  | "paused"
  | "done"
  | "failed"
  | "cancelled";
export type MissionAutonomy = "ask_first" | "checkpoints" | "autonomous";
export interface Mission {
  id: string;
  project_id: string;
  created_by: string;
  title: string;
  objective: string;
  success_criteria?: string;
  status: MissionStatus;
  pause_reason?: string;
  autonomy: MissionAutonomy;
  phase: string;
  next_tick_at?: string;
  budget_uc: number;
  spent_uc: number;
  tick_cap: number;
  ticks_used: number;
  held_tool_name?: string;
  conversation_id?: string; // the dashboard chat session that started it (scoping)
  tasks_total?: number; // plan progress (computed on list)
  tasks_done?: number;
  current_run_id?: string;
  last_activity_at?: string;
  created_at: string;
  updated_at: string;
}
export interface MissionTask {
  id: string;
  seq: number;
  title?: string; // short label shown in the plan list (description is the expandable detail)
  description: string;
  status: string; // pending | doing | blocked | done | failed
  deps?: string[];
  result_ref?: string;
  attempts: number;
  created_at: string;
  updated_at: string;
}
export interface MissionEvent {
  id: string;
  ts: string;
  kind: string;
  summary: string;
  ref?: string;
}
export interface MissionDetailView {
  mission: Mission;
  tasks: MissionTask[];
  events: MissionEvent[];
}
export interface MissionCreateInput {
  objective: string;
  title?: string;
  success_criteria?: string;
  autonomy?: MissionAutonomy;
  budget_credits?: number; // 0/omitted → inherit the agent budget / default
  tick_cap?: number;
  channel_id?: string; // optional agent-role channel to notify on state changes
}
const missionPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/missions`;
const missionOne = (projectId: string, id: string) =>
  `${missionPath(projectId)}/${encodeURIComponent(id)}`;
export const listMissions = (projectId: string) =>
  req<{ missions: Mission[] }>("GET", missionPath(projectId)).then((r) => r.missions ?? []);
export const createMission = (projectId: string, input: MissionCreateInput) =>
  req<Mission>("POST", missionPath(projectId), input);
export const getMission = (projectId: string, id: string) =>
  req<MissionDetailView>("GET", missionOne(projectId, id));
export const cancelMission = (projectId: string, id: string) =>
  req<Mission>("DELETE", missionOne(projectId, id));
export const pauseMission = (projectId: string, id: string) =>
  req<Mission>("POST", `${missionOne(projectId, id)}/pause`);
export const resumeMission = (projectId: string, id: string) =>
  req<Mission>("POST", `${missionOne(projectId, id)}/resume`);
export const approveMissionPlan = (projectId: string, id: string) =>
  req<Mission>("POST", `${missionOne(projectId, id)}/approve-plan`);
export const rejectMissionPlan = (projectId: string, id: string, reason?: string) =>
  req<Mission>("POST", `${missionOne(projectId, id)}/reject-plan`, { reason: reason ?? "" });
// replyMissionPlan sends a freeform reply to a pending plan; the server reads the
// intent (a small model) and runs approve / revise / discard, returning which it did.
export const replyMissionPlan = (projectId: string, id: string, message: string) =>
  req<{ action: "approve" | "revise" | "discard"; mission: Mission }>("POST", `${missionOne(projectId, id)}/reply`, { message });
export const steerMission = (projectId: string, id: string, message: string) =>
  req<{ steered: boolean }>("POST", `${missionOne(projectId, id)}/steer`, { message });
// budgetCredits = 0 removes the per-mission limit (workspace credits still apply).
export const setMissionBudget = (projectId: string, id: string, budgetCredits: number) =>
  req<Mission>("POST", `${missionOne(projectId, id)}/budget`, { budget_credits: budgetCredits });

// Platform-run agent runtime (the dashboard chat playground).
export type AgentRunStatus = "runnable" | "running" | "awaiting_human" | "done" | "failed";
export interface AgentFileRef {
  path: string; // relative to the state volume, e.g. "files/chart.png" or "views/<id>/view.tsx"
  mime: string;
  name: string;
  // kind: "" (file/image) or "view" (a generative-UI page); url is the served
  // view URL (isolated origin) — present only for kind:"view".
  kind?: string;
  url?: string;
}
export interface AgentMessageView {
  role: string;
  text: string;
  // File artifacts (e.g. a generated chart) this turn produced — REFERENCES to
  // files saved on the deployment's volume; the bytes are fetched on demand via
  // fetchAgentFile (not stored in the run record).
  files?: AgentFileRef[];
  // Set when this is a mission's terminal result message — the chat renders the
  // mission's breakdown card above it (persistent in history).
  mission_id?: string;
}
// fetchAgentFile streams an agent file artifact (state/files/<…>) as a Blob, with
// the session cookie (the asset endpoint is auth'd + path-confined to files/).
export async function fetchAgentFile(projectId: string, filePath: string): Promise<Blob> {
  const res = await fetch(
    `${BASE}/v1/projects/${encodeURIComponent(projectId)}/agent/file?path=${encodeURIComponent(filePath)}`,
    { credentials: "include" },
  );
  if (!res.ok) throw new APIError(res.status, `file ${res.status}`);
  return res.blob();
}
export interface AgentRunView {
  id: string;
  status: AgentRunStatus;
  messages: AgentMessageView[];
}
const agentPath = (projectId: string) => `/v1/projects/${encodeURIComponent(projectId)}/agent`;
// mode "mission" deterministically starts a mission from this message (no agent
// task-vs-mission judgment); omit for a normal chat turn.
export const postAgentMessage = (projectId: string, conversationId: string, text: string, mode?: "mission") =>
  req<{ run_id: string; status: AgentRunStatus }>("POST", `${agentPath(projectId)}/messages`, {
    conversation_id: conversationId,
    text,
    mode,
  });
export const getAgentRun = (projectId: string, runId: string) =>
  req<AgentRunView>("GET", `${agentPath(projectId)}/runs/${encodeURIComponent(runId)}`);
// Stop an in-progress run (the chat Stop button). Idempotent.
export const cancelAgentRun = (projectId: string, runId: string) =>
  req<{ cancelled: boolean }>("POST", `${agentPath(projectId)}/runs/${encodeURIComponent(runId)}/cancel`);
// SSE endpoint URL (consumed via EventSource with credentials).
export const agentRunStreamURL = (projectId: string, runId: string) =>
  `${apiBase}${agentPath(projectId)}/runs/${encodeURIComponent(runId)}/stream`;

// Chat history: a project's dashboard conversations (browse / continue / delete).
export interface AgentRunSummary {
  id: string;
  conversation_id: string;
  status: AgentRunStatus;
  title: string;
  preview: string;
  message_count: number;
  created_at: string;
  updated_at: string;
}
export const listAgentRuns = (projectId: string) =>
  req<{ runs: AgentRunSummary[] }>("GET", `${agentPath(projectId)}/runs`).then((r) => r.runs ?? []);
export const deleteAgentRun = (projectId: string, runId: string) =>
  req<{ deleted: boolean }>("DELETE", `${agentPath(projectId)}/runs/${encodeURIComponent(runId)}`);
export const createPolicyRule = (projectId: string, rule: PolicyRuleInput) =>
  req<{ rule: PolicyRule }>("POST", `${polPath(projectId)}/rules`, rule);
export const updatePolicyRule = (projectId: string, id: string, rule: PolicyRuleInput) =>
  req<{ rule: PolicyRule }>("PUT", `${polPath(projectId)}/rules/${encodeURIComponent(id)}`, rule);
export const deletePolicyRule = (projectId: string, id: string) =>
  req("DELETE", `${polPath(projectId)}/rules/${encodeURIComponent(id)}`);

// applyGovernance applies a reviewed GovernancePlan to a freshly-created project,
// in the only order the engine allows: channel (if the user supplied its secret)
// → default posture → rules (HOLD rules route to the channel) → mode LAST (only
// if the user opted into enforce). Partial-failure tolerant: it never throws;
// it returns a list of human-readable warnings for the steps that failed, so the
// caller can land the user on the created agent with a "finish in Policy tab"
// note rather than rolling back. enforce is honored ONLY when explicitly true.
export interface GovernanceApply {
  plan: GovernancePlan;
  // The Telegram secret the USER typed at review (the AI never provides it). When
  // absent, the channel is skipped and HOLD rules are created channel-less
  // (audit-safe; the user can wire a channel later).
  channelSecret?: { bot_token: string; chat_id: string } | null;
  enforce: boolean;
}
export async function applyGovernance(projectId: string, a: GovernanceApply): Promise<string[]> {
  const warnings: string[] = [];
  const { plan } = a;

  // 1) channel first — HOLD rules reference its id.
  let channelId: string | null = null;
  if (plan.channel && a.channelSecret?.bot_token && a.channelSecret?.chat_id) {
    try {
      const r = await createChannel(projectId, {
        name: plan.channel.name,
        kind: plan.channel.kind,
        telegram: { bot_token: a.channelSecret.bot_token, chat_id: a.channelSecret.chat_id },
      });
      channelId = r.channel.id;
    } catch {
      warnings.push("Couldn't create the channel — add it in the Channels tab.");
    }
  }

  // 2) default posture (unless plain allow).
  let defaultOk = true;
  if (plan.default_action) {
    try {
      await setPolicyDefault(projectId, plan.default_action);
    } catch {
      defaultOk = false;
      warnings.push("Couldn't set the default policy — set it in the Policy tab.");
    }
  }

  // 3) rules. HOLD rules route to the channel when we have one.
  let ruleFailures = 0;
  for (let i = 0; i < plan.rules.length; i++) {
    const rule = plan.rules[i];
    try {
      await createPolicyRule(projectId, {
        priority: i,
        match: { tool: rule.tool },
        action: rule.action,
        reason: rule.reason ?? "",
        enabled: true,
        approval: rule.action === "HOLD" && channelId ? { channels: [channelId] } : undefined,
      });
    } catch {
      ruleFailures++;
    }
  }
  if (ruleFailures > 0) {
    warnings.push(`${ruleFailures} of ${plan.rules.length} policy rules couldn't be created — review them in the Policy tab.`);
  }

  // 4) mode LAST — and only if the user opted in AND the posture it depends on
  // actually landed. Enforcing a DENY/HOLD default whose ALLOW exceptions failed
  // to create would block (or hold) every call on the brand-new agent, so on a
  // partial failure we keep it in audit mode and tell the user to finish + flip
  // it themselves.
  if (a.enforce) {
    if (defaultOk && ruleFailures === 0) {
      try {
        await setPolicyMode(projectId, "enforce");
      } catch {
        warnings.push("Couldn't switch to enforce — flip it on in the Policy tab once you've checked the rules.");
      }
    } else {
      warnings.push(
        "Left the agent in audit mode because some policy settings didn't apply — fix them, then enable enforce in the Policy tab.",
      );
    }
  }
  return warnings;
}

// --- HOLD approval decisions ---
export interface PendingDecision {
  ticket_id: string;
  // kind "" = approval HOLD (approve/reject); "question" = Ask-a-human (the
  // human types a freeform answer). question carries the escalated text.
  kind?: string;
  question?: string;
  tool: string;
  principal?: string;
  reason?: string;
  rule_id?: string;
  created_at: string;
  expires_at: string;
  notified_channels?: string[];
  args_preview?: string; // admins only
}
export const listDecisions = (projectId: string) =>
  req<{ decisions: PendingDecision[]; can_resolve?: boolean }>(
    "GET",
    `${polPath(projectId)}/decisions`,
  ).then((r) => ({ decisions: r.decisions ?? [], canResolve: !!r.can_resolve }));
export const resolveDecision = (projectId: string, ticket: string, approve: boolean) =>
  req<{ state: string }>(
    "POST",
    `${polPath(projectId)}/decisions/${encodeURIComponent(ticket)}/resolve`,
    { approve },
  );
// answerDecision submits a human's freeform answer to an Ask-a-human question
// ticket (kind === "question"), vs resolveDecision's approve/reject.
export const answerDecision = (projectId: string, ticket: string, answer: string) =>
  req<{ state: string }>(
    "POST",
    `${polPath(projectId)}/decisions/${encodeURIComponent(ticket)}/answer`,
    { answer },
  );
// dismissDecision clears a pending ticket (question or approval) to the terminal
// expired state without answering/approving — for stale/no-longer-relevant ones.
export const dismissDecision = (projectId: string, ticket: string) =>
  req<{ dismissed: boolean }>(
    "POST",
    `${polPath(projectId)}/decisions/${encodeURIComponent(ticket)}/dismiss`,
  );

// --- audit log (per-project tool-call activity) ---
export interface AuditEntry {
  at: string;
  principal?: string;
  tool: string;
  status: string; // ok | denied | error | unknown_principal | approved | rejected
  error?: string; // scrubbed failure reason (denied/error rows)
  verdict?: string; // ALLOW | DENY | HOLD
  rule_id?: string;
  ticket_id?: string;
  approver?: string; // resolver user id (raw)
  approver_label?: string; // human-readable resolver name/email (server-enriched)
  latency_ms: number;
  request?: string; // captured tool args (admins only, when capture is on)
}
export const listAudit = (projectId: string, limit = 100) =>
  req<{ entries: AuditEntry[]; can_view_payloads?: boolean }>(
    "GET",
    `/v1/projects/${encodeURIComponent(projectId)}/audit?limit=${limit}`,
  ).then((r) => ({ entries: r.entries ?? [], canViewPayloads: !!r.can_view_payloads }));
